import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchScrapCostCenters,
    fetchScrapClients,
    fetchScrapSubClients,
    fetchScrapRequestNumbers,
    fetchScrapRequestDetails,
    fetchScrapClientGSTNos,
    fetchScrapCompanyGSTNos,
    fetchScrapTCSDCASDCAS,
    submitScrapInvoice,
    clearScrapSaveResult,
    clearScrapSubClients,
    clearScrapRequestNumbers,
    clearScrapRequestDetails,
    resetScrapInvoice,
    selectScrapCostCenters,
    selectScrapClients,
    selectScrapSubClients,
    selectScrapRequestNumbers,
    selectScrapRequestDetails,
    selectScrapClientGSTNos,
    selectScrapCompanyGSTNos,
    selectScrapTCSDCASDCAS,
    selectScrapSaveResult,
    selectScrapCCLoading,
    selectScrapClientsLoading,
    selectScrapSubCliLoading,
    selectScrapReqNosLoading,
    selectScrapReqDetailsLoading,
    selectScrapGSTLoading,
    selectScrapSaveLoading,
    selectScrapSaveError,
} from '../../slices/accountsSlice/clientScrapSaleInvoiceSlice';

import {
    ReceiptText, IndianRupee,
    ShieldCheck, Percent, CheckCircle,
    Loader2, ChevronDown, RotateCcw, Send,
    Recycle,
} from 'lucide-react';

// ── Constants ─────────────────────────────────────────────────────────────────
const INVOICE_TYPES = [
    { value: 'Service',       label: 'Invoice Service'  },
    { value: 'Manufacturing', label: 'Manufacturing'    },
];

const INV_TYPE = 'Scrap';

// ── Helpers ───────────────────────────────────────────────────────────────────
const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const formatDateForAPI = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && /^\d{2}-[A-Za-z]{3}-\d{4}$/.test(val)) return val;
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
        const [yyyy, mm, dd] = val.split('T')[0].split('-');
        return `${dd}-${MONTH_ABBR[parseInt(mm, 10) - 1]}-${yyyy}`;
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2,'0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

const fmt = (v) => {
    const n = parseFloat(v);
    if ((!v && v !== 0) || isNaN(n)) return '0.00';
    return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const calcTotal = (basicValue, form) => {
    let total = parseFloat(basicValue) || 0;
    if (form.IsGstApplicable === 'Yes') {
        if (form.Statecheck) {
            total += (parseFloat(form.Cgstsdcaamt) || 0) + (parseFloat(form.Sgstsdcaamt) || 0);
        } else {
            total += parseFloat(form.Igstsdcaamt) || 0;
        }
    }
    if (form.IsTCSApplicable === 'Yes') {
        total += parseFloat(form.TCSAmount) || 0;
    }
    return total;
};

const buildTaxStrings = (form) => {
    if (form.IsGstApplicable !== 'Yes') return { Taxtypes: null, Taxdcas: null, Taxamounts: null };
    if (form.Statecheck) {
        return {
            Taxtypes:   'CGST,SGST',
            Taxdcas:    `${form.CgstDca},${form.SgstDca}`,
            Taxamounts: `${parseFloat(form.Cgstsdcaamt) || 0},${parseFloat(form.Sgstsdcaamt) || 0}`,
        };
    }
    return {
        Taxtypes:   'IGST',
        Taxdcas:    form.IgstDca,
        Taxamounts: String(parseFloat(form.Igstsdcaamt) || 0),
    };
};

// ── Shared UI primitives (compact) ────────────────────────────────────────────
const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 hover:border-gray-400 transition-all disabled:opacity-60 disabled:cursor-not-allowed';

const Label = ({ children, required }) => (
    <label className="block text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectIcon = ({ loading }) => (
    <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
        {loading
            ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" />
            : <ChevronDown className="h-4 w-4 text-gray-400" />}
    </div>
);

const CardHeader = ({ icon: Icon, title, action }) => (
    <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/60 dark:bg-gray-900/40 rounded-t-xl">
        <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-indigo-500" />
            <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">{title}</h2>
        </div>
        {action}
    </div>
);

const RupeeInput = ({ value, onChange, disabled }) => (
    <div className="relative">
        <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        <input type="number" placeholder="0.00" value={value} onChange={onChange} disabled={disabled} className={`${inputCls} pl-8`} />
    </div>
);

// Small inline Yes/No segmented toggle
const YesNoToggle = ({ value, onChange }) => (
    <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
        {['Yes', 'No'].map(opt => (
            <button
                key={opt}
                type="button"
                onClick={() => onChange(opt)}
                className={`px-4 py-1 text-xs font-semibold transition-all ${
                    value === opt
                        ? opt === 'Yes'
                            ? 'bg-green-500 text-white'
                            : 'bg-rose-500 text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
            >
                {opt}
            </button>
        ))}
    </div>
);

// ── Initial state ──────────────────────────────────────────────────────────────
const INITIAL_FORM = {
    InvoiceSubType:    '',
    CCCode:            '',
    RANO:              '',
    Clientcode:        '',
    SubClientcode:     '',
    RequestNumber:     '',
    RequestId:         '',
    ClientInvoiceNo:   '',
    InvoiceDate:       '',
    InvoiceMakingDate: '',
    InvoiceRemarks:    '',
    IsGstApplicable:   '',
    TaxId:             0,
    ClientGST:         '',
    CompanyGST:        '',
    Statecheck:        false,
    CgstDca:           '',
    Cgstsdca:          '',
    Cgstsdcaamt:       '',
    SgstDca:           '',
    Sgstsdca:          '',
    Sgstsdcaamt:       '',
    IgstDca:           '',
    Igstsdca:          '',
    Igstsdcaamt:       '',
    IsTCSApplicable:   '',
    TCSDCA:            '',
    TCSSDCA:           '',
    TCSAmount:         '',
};

// ── Component ─────────────────────────────────────────────────────────────────
const ClientScrapSaleInvoiceCreation = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector(s => s.auth);
    const roleId = userData?.roleId  || userData?.RID  || 0;
    const userId = userData?.userId  || userData?.UID  || userData?.employeeId || '';

    // Redux state
    const costCenters     = useSelector(selectScrapCostCenters);
    const clients         = useSelector(selectScrapClients);
    const subClients      = useSelector(selectScrapSubClients);
    const requestNumbers  = useSelector(selectScrapRequestNumbers);
    const requestDetails  = useSelector(selectScrapRequestDetails);
    const clientGSTNos    = useSelector(selectScrapClientGSTNos);
    const companyGSTNos   = useSelector(selectScrapCompanyGSTNos);
    const tcsDCASDCAS     = useSelector(selectScrapTCSDCASDCAS);
    const saveResult      = useSelector(selectScrapSaveResult);

    const ccLoading         = useSelector(selectScrapCCLoading);
    const clientsLoading    = useSelector(selectScrapClientsLoading);
    const subCliLoading     = useSelector(selectScrapSubCliLoading);
    const reqNosLoading     = useSelector(selectScrapReqNosLoading);
    const reqDetailsLoading = useSelector(selectScrapReqDetailsLoading);
    const gstLoading        = useSelector(selectScrapGSTLoading);
    const saveLoading       = useSelector(selectScrapSaveLoading);
    const saveError         = useSelector(selectScrapSaveError);

    // Local state
    const [form, setForm] = useState(INITIAL_FORM);
    const [basicValue, setBasicValue] = useState('');

    // ── Init ──────────────────────────────────────────────────────────────────
    useEffect(() => {
        dispatch(fetchScrapCompanyGSTNos('GST'));
        dispatch(fetchScrapTCSDCASDCAS());
    }, [dispatch]);

    // Pre-fill BasicValue when request details load, but keep it editable
    useEffect(() => {
        if (requestDetails?.BasicValue !== undefined && requestDetails?.BasicValue !== null) {
            setBasicValue(String(requestDetails.BasicValue));
        }
    }, [requestDetails]);

    // Pre-fill TCS DCA/SDCA once loaded
    useEffect(() => {
        if (tcsDCASDCAS) {
            setForm(p => ({
                ...p,
                TCSDCA:  tcsDCASDCAS.TCSDCA  || '',
                TCSSDCA: tcsDCASDCAS.TCSSDCA || '',
            }));
        }
    }, [tcsDCASDCAS]);

    // Detect intra/inter state from GST numbers
    useEffect(() => {
        if (form.ClientGST && form.CompanyGST) {
            const clientState  = form.ClientGST.substring(0, 2);
            const companyState = form.CompanyGST.substring(0, 2);
            setForm(p => ({ ...p, Statecheck: clientState === companyState }));
        }
    }, [form.ClientGST, form.CompanyGST]);

    // Handle save result
    useEffect(() => {
        if (!saveResult) return;
        const msg = typeof saveResult === 'string' ? saveResult : saveResult?.Message || '';
        if (msg === 'Submited' || msg.toLowerCase().includes('success')) {
            toast.success('Scrap sale invoice submitted successfully!');
            handleReset();
        } else if (msg) {
            toast.error(msg);
        }
        dispatch(clearScrapSaveResult());
    }, [saveResult]);

    useEffect(() => {
        if (saveError) toast.error(saveError);
    }, [saveError]);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleInvoiceTypeChange = (subType) => {
        setForm(p => ({
            ...INITIAL_FORM,
            InvoiceSubType: subType,
            TCSDCA:  p.TCSDCA,
            TCSSDCA: p.TCSSDCA,
        }));
        dispatch(clearScrapSubClients());
        dispatch(clearScrapRequestDetails());
        if (subType) {
            dispatch(fetchScrapCostCenters(subType));
        }
    };

    const handleCCChange = (ccCode) => {
        setForm(p => ({
            ...INITIAL_FORM,
            InvoiceSubType: p.InvoiceSubType,
            CCCode:  ccCode,
            TCSDCA:  p.TCSDCA,
            TCSSDCA: p.TCSSDCA,
        }));
        dispatch(clearScrapSubClients());
        dispatch(clearScrapRequestDetails());
        if (ccCode) {
            dispatch(fetchScrapClients({ invType: INV_TYPE, ccCode }));
        }
    };

    const handleClientChange = (clientcode) => {
        setForm(p => ({ ...p, Clientcode: clientcode, SubClientcode: '', RequestNumber: '', RequestId: '' }));
        dispatch(clearScrapSubClients());
        if (clientcode && form.CCCode) {
            dispatch(fetchScrapSubClients({ invType: INV_TYPE, ccCode: form.CCCode, clientcode }));
        }
    };

    const handleSubClientChange = (subClientCode) => {
        setForm(p => ({ ...p, SubClientcode: subClientCode, RequestNumber: '', RequestId: '' }));
        setBasicValue('');
        dispatch(clearScrapRequestNumbers());
        dispatch(clearScrapRequestDetails());
        if (subClientCode && form.CCCode && form.Clientcode) {
            dispatch(fetchScrapRequestNumbers({
                subClient:  subClientCode,
                ccCode:     form.CCCode,
                clientcode: form.Clientcode,
            }));
            dispatch(fetchScrapClientGSTNos({ taxtype: 'SubClient', taxfor: subClientCode }));
        }
    };

    const handleRequestChange = (reqId) => {
        const selected = requestNumbers.find(r => String(r.RequestId) === String(reqId));
        setForm(p => ({
            ...p,
            RequestId:     reqId,
            RequestNumber: selected?.RequestNumber || '',
        }));
        dispatch(clearScrapRequestDetails());
        if (reqId) {
            dispatch(fetchScrapRequestDetails(reqId));
        }
    };

    const handleReset = () => {
        setForm(prev => ({
            ...INITIAL_FORM,
            TCSDCA:  tcsDCASDCAS?.TCSDCA  || '',
            TCSSDCA: tcsDCASDCAS?.TCSSDCA || '',
        }));
        setBasicValue('');
        dispatch(clearScrapRequestDetails());
        dispatch(fetchScrapCompanyGSTNos('GST'));
        dispatch(fetchScrapTCSDCASDCAS());
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = () => {
        if (!form.InvoiceSubType)    { toast.warn('Select an Invoice Type');          return; }
        if (!form.CCCode)            { toast.warn('Select a Cost Center');            return; }
        if (!form.Clientcode)        { toast.warn('Select a Client');                 return; }
        if (!form.SubClientcode)     { toast.warn('Select a Sub-Client');             return; }
        if (!form.RequestId)         { toast.warn('Select a Request Number');         return; }
        if (!(parseFloat(basicValue) > 0)) { toast.warn('Enter a valid Basic Value');  return; }
        if (!form.ClientInvoiceNo)   { toast.warn('Enter Client Invoice Number');     return; }
        if (!form.InvoiceDate)       { toast.warn('Select Invoice Date');             return; }
        if (!form.InvoiceMakingDate) { toast.warn('Select Invoice Making Date');      return; }
        if (!form.IsGstApplicable)   { toast.warn('Specify if GST is applicable');    return; }
        if (form.IsGstApplicable === 'Yes') {
            if (!form.ClientGST)  { toast.warn('Select Client GST Number');  return; }
            if (!form.CompanyGST) { toast.warn('Select Company GST Number'); return; }
        }
        if (!form.IsTCSApplicable)   { toast.warn('Specify if TCS is applicable');   return; }

        const bv         = parseFloat(basicValue) || 0;
        const taxStrings = buildTaxStrings(form);
        const total      = calcTotal(bv, form);

        dispatch(submitScrapInvoice({
            RequestNumber:     form.RequestNumber,
            RANO:              form.RANO || null,
            CCCode:            form.CCCode,
            ClientInvoiceNo:   form.ClientInvoiceNo,
            InvoiceDate:       formatDateForAPI(form.InvoiceDate),
            InvoiceMakingDate: formatDateForAPI(form.InvoiceMakingDate),
            BasicValue:        bv,
            Total:             total,
            InvoiceRemarks:    form.InvoiceRemarks || null,
            InvoiceType:       INV_TYPE,
            Clientcode:        form.Clientcode,
            SubClientcode:     form.SubClientcode,
            InvoiceSubType:    form.InvoiceSubType,
            CreatedBy:         userData?.userName || "",
            Roleid:            roleId,
            Taxtypes:          taxStrings.Taxtypes,
            Taxdcas:           taxStrings.Taxdcas,
            Taxamounts:        taxStrings.Taxamounts,
            TaxId:             form.TaxId || 0,
            ClientGST:         form.IsGstApplicable === 'Yes' ? form.ClientGST  : null,
            CompanyGST:        form.IsGstApplicable === 'Yes' ? form.CompanyGST : null,
            Statecheck:        form.IsGstApplicable === 'Yes' ? form.Statecheck  : false,
            Cgstsdca:          form.Cgstsdca   || null,
            Cgstsdcaamt:       parseFloat(form.Cgstsdcaamt)  || 0,
            Sgstsdca:          form.Sgstsdca   || null,
            Sgstsdcaamt:       parseFloat(form.Sgstsdcaamt)  || 0,
            Igstsdca:          form.Igstsdca   || null,
            Igstsdcaamt:       parseFloat(form.Igstsdcaamt)  || 0,
            IsGstApplicable:   form.IsGstApplicable || null,
            IsTCSApplicable:   form.IsTCSApplicable || null,
            TCSDCA:            form.IsTCSApplicable === 'Yes' ? form.TCSDCA   : null,
            TCSSDCA:           form.IsTCSApplicable === 'Yes' ? form.TCSSDCA  : null,
            TCSAmount:         form.IsTCSApplicable === 'Yes' ? (parseFloat(form.TCSAmount) || 0) : 0,
        }));
    };

    // ── Derived option lists ──────────────────────────────────────────────────
    const ccOptions         = costCenters.map(c => ({ value: c.CC_Code || c.CCCode, label: c.CC_Name || c.CCName }));
    const clientOptions     = clients.map(c => ({ value: c.ClientCode || c.Clientcode, label: c.ClientName || c.Clientname }));
    const subClientOptions  = subClients.map(c => ({
        value: c.SubClientCode?.split(',')[0]?.trim() || c.SubClientcode || c.SubClientCode,
        label: c.SubClientCode || c.SubClientcode,
    }));
    const requestOptions    = requestNumbers.map(r => ({ value: r.RequestId, label: r.RequestNumber }));
    const clientGSTOptions  = clientGSTNos.map(g => ({ value: g.TaxNoName, label: g.TaxNoName, taxId: g.TaxNoID }));
    const companyGSTOptions = companyGSTNos.map(g => ({ value: g.GSTNo || g.TaxNoName, label: g.TaxNoName }));

    const bvNum       = parseFloat(basicValue) || 0;
    const total       = calcTotal(bvNum, form);
    const isBusy      = saveLoading;
    const gstDetected = form.ClientGST && form.CompanyGST;

    // Breakdown for the summary panel — mirrors calcTotal
    const gstAmt = form.IsGstApplicable === 'Yes'
        ? (form.Statecheck
            ? (parseFloat(form.Cgstsdcaamt) || 0) + (parseFloat(form.Sgstsdcaamt) || 0)
            : (parseFloat(form.Igstsdcaamt) || 0))
        : 0;
    const tcsAmt = form.IsTCSApplicable === 'Yes' ? (parseFloat(form.TCSAmount) || 0) : 0;
    const clientLabel = clientOptions.find(c => c.value === form.Clientcode)?.label || form.Clientcode;

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-3 md:p-4">

            {/* ── Page Header (slim) ───────────────────────────────────────── */}
            <div className="max-w-7xl mx-auto mb-4">
                <div className="rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-md shadow-indigo-500/20 px-5 py-3 text-white flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center border border-white/20">
                        <Recycle className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-lg font-black tracking-tight leading-tight truncate">
                            {menuData?.name || 'Client Scrap Sale Invoice'}
                        </h1>
                        <p className="text-indigo-200 text-xs">Accounts Module · Create scrap sale invoice with GST and TCS details</p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">

                {/* ── Left: form ───────────────────────────────────────────── */}
                <div className="lg:col-span-2 space-y-4">

                    {/* Invoice details — setup, client/request, value, dates, remarks */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <CardHeader icon={ReceiptText} title="Invoice Details" />
                        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
                            <div>
                                <Label required>Invoice Type</Label>
                                <div className="relative">
                                    <select
                                        value={form.InvoiceSubType}
                                        onChange={e => handleInvoiceTypeChange(e.target.value)}
                                        disabled={isBusy}
                                        className={`${inputCls} appearance-none pr-8`}
                                    >
                                        <option value="">— Select Type —</option>
                                        {INVOICE_TYPES.map(t => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </select>
                                    <SelectIcon loading={false} />
                                </div>
                            </div>
                            <div>
                                <Label required>Cost Center</Label>
                                <div className="relative">
                                    <select
                                        value={form.CCCode}
                                        onChange={e => handleCCChange(e.target.value)}
                                        disabled={!form.InvoiceSubType || ccLoading || isBusy}
                                        className={`${inputCls} appearance-none pr-8`}
                                    >
                                        <option value="">
                                            {!form.InvoiceSubType ? 'Select type first' : ccLoading ? 'Loading…' : '— Select CC —'}
                                        </option>
                                        {ccOptions.map(c => (
                                            <option key={c.value} value={c.value}>{c.label}</option>
                                        ))}
                                    </select>
                                    <SelectIcon loading={ccLoading} />
                                </div>
                            </div>
                            <div>
                                <Label>RANO</Label>
                                <input
                                    type="text"
                                    placeholder="Enter RANO reference…"
                                    value={form.RANO}
                                    onChange={e => setForm(p => ({ ...p, RANO: e.target.value }))}
                                    disabled={isBusy}
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <Label required>Client</Label>
                                <div className="relative">
                                    <select
                                        value={form.Clientcode}
                                        onChange={e => handleClientChange(e.target.value)}
                                        disabled={!form.CCCode || clientsLoading || isBusy}
                                        className={`${inputCls} appearance-none pr-8`}
                                    >
                                        <option value="">{!form.CCCode ? 'Select CC first' : clientsLoading ? 'Loading…' : '— Select Client —'}</option>
                                        {clientOptions.map(c => (
                                            <option key={c.value} value={c.value}>{c.label}</option>
                                        ))}
                                    </select>
                                    <SelectIcon loading={clientsLoading} />
                                </div>
                            </div>
                            <div>
                                <Label required>Sub Client</Label>
                                <div className="relative">
                                    <select
                                        value={form.SubClientcode}
                                        onChange={e => handleSubClientChange(e.target.value)}
                                        disabled={!form.Clientcode || subCliLoading || isBusy}
                                        className={`${inputCls} appearance-none pr-8`}
                                    >
                                        <option value="">{!form.Clientcode ? 'Select client first' : subCliLoading ? 'Loading…' : '— Select Sub Client —'}</option>
                                        {subClientOptions.map(c => (
                                            <option key={c.value} value={c.value}>{c.label}</option>
                                        ))}
                                    </select>
                                    <SelectIcon loading={subCliLoading} />
                                </div>
                            </div>
                            <div>
                                <Label required>Request Number</Label>
                                <div className="relative">
                                    <select
                                        value={form.RequestId}
                                        onChange={e => handleRequestChange(e.target.value)}
                                        disabled={!form.SubClientcode || reqNosLoading || isBusy}
                                        className={`${inputCls} appearance-none pr-8`}
                                    >
                                        <option value="">{!form.SubClientcode ? 'Select sub-client first' : reqNosLoading ? 'Loading…' : '— Select Request —'}</option>
                                        {requestOptions.map(r => (
                                            <option key={r.value} value={r.value}>{r.label}</option>
                                        ))}
                                    </select>
                                    <SelectIcon loading={reqNosLoading} />
                                </div>
                            </div>

                            {/* Basic Value — pre-filled from request, editable */}
                            <div>
                                <Label required>Basic Value</Label>
                                {reqDetailsLoading ? (
                                    <div className={`${inputCls} flex items-center gap-2 text-gray-400`}>
                                        <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" />
                                        <span>Loading…</span>
                                    </div>
                                ) : (
                                    <RupeeInput
                                        value={basicValue}
                                        onChange={e => setBasicValue(e.target.value)}
                                        disabled={isBusy}
                                    />
                                )}
                            </div>
                            <div>
                                <Label required>Client Invoice No.</Label>
                                <input
                                    type="text"
                                    placeholder="Enter invoice number…"
                                    value={form.ClientInvoiceNo}
                                    onChange={e => setForm(p => ({ ...p, ClientInvoiceNo: e.target.value }))}
                                    disabled={isBusy}
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <Label required>Invoice Date</Label>
                                <CustomDatePicker
                                    value={form.InvoiceDate}
                                    onChange={val => setForm(p => ({ ...p, InvoiceDate: val }))}
                                    placeholder="Select invoice date"
                                    disabled={isBusy}
                                />
                            </div>
                            <div>
                                <Label required>Invoice Making Date</Label>
                                <CustomDatePicker
                                    value={form.InvoiceMakingDate}
                                    onChange={val => setForm(p => ({ ...p, InvoiceMakingDate: val }))}
                                    placeholder="Select making date"
                                    disabled={isBusy}
                                />
                            </div>
                            <div className="sm:col-span-2 lg:col-span-2">
                                <Label>Invoice Remarks</Label>
                                <textarea
                                    rows={1}
                                    placeholder="Enter remarks or additional notes (optional)…"
                                    value={form.InvoiceRemarks}
                                    onChange={e => setForm(p => ({ ...p, InvoiceRemarks: e.target.value }))}
                                    disabled={isBusy}
                                    className={`${inputCls} resize-y`}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Tax — GST and TCS side by side */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <CardHeader icon={ShieldCheck} title="Tax Configuration" />
                        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100 dark:divide-gray-700">

                            {/* GST */}
                            <div className="p-4 space-y-3">
                                <div className="flex items-center justify-between gap-3">
                                    <span className="flex items-center gap-1.5 text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                                        <ShieldCheck className="w-3.5 h-3.5 text-indigo-500" /> GST Applicable?<span className="text-rose-500">*</span>
                                    </span>
                                    <YesNoToggle value={form.IsGstApplicable} onChange={v => setForm(p => ({ ...p, IsGstApplicable: v }))} />
                                </div>

                                {form.IsGstApplicable === 'Yes' && (
                                    <>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div>
                                                <Label required>Client GST</Label>
                                                <div className="relative">
                                                    <select
                                                        value={form.ClientGST}
                                                        onChange={e => {
                                                            const opt = clientGSTOptions.find(g => g.value === e.target.value);
                                                            setForm(p => ({ ...p, ClientGST: e.target.value, TaxId: opt?.taxId || 0 }));
                                                        }}
                                                        disabled={gstLoading || isBusy}
                                                        className={`${inputCls} appearance-none pr-8`}
                                                    >
                                                        <option value="">{gstLoading ? 'Loading…' : '— Select —'}</option>
                                                        {clientGSTOptions.map(g => (
                                                            <option key={g.value} value={g.value}>{g.label}</option>
                                                        ))}
                                                    </select>
                                                    <SelectIcon loading={gstLoading} />
                                                </div>
                                            </div>
                                            <div>
                                                <Label required>Company GST</Label>
                                                <div className="relative">
                                                    <select
                                                        value={form.CompanyGST}
                                                        onChange={e => setForm(p => ({ ...p, CompanyGST: e.target.value }))}
                                                        disabled={gstLoading || isBusy}
                                                        className={`${inputCls} appearance-none pr-8`}
                                                    >
                                                        <option value="">{gstLoading ? 'Loading…' : '— Select —'}</option>
                                                        {companyGSTOptions.map(g => (
                                                            <option key={g.value} value={g.value}>{g.label}</option>
                                                        ))}
                                                    </select>
                                                    <SelectIcon loading={gstLoading} />
                                                </div>
                                            </div>
                                        </div>

                                        {gstDetected && (
                                            <>
                                                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold ${
                                                    form.Statecheck
                                                        ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800'
                                                        : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800'
                                                }`}>
                                                    <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />
                                                    {form.Statecheck ? 'Same State — CGST + SGST applicable' : 'Inter-State — IGST applicable'}
                                                </div>

                                                {form.Statecheck ? (
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <div>
                                                            <Label>CGST Amount</Label>
                                                            <RupeeInput value={form.Cgstsdcaamt} onChange={e => setForm(p => ({ ...p, Cgstsdcaamt: e.target.value }))} disabled={isBusy} />
                                                        </div>
                                                        <div>
                                                            <Label>SGST Amount</Label>
                                                            <RupeeInput value={form.Sgstsdcaamt} onChange={e => setForm(p => ({ ...p, Sgstsdcaamt: e.target.value }))} disabled={isBusy} />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="sm:w-1/2">
                                                        <Label>IGST Amount</Label>
                                                        <RupeeInput value={form.Igstsdcaamt} onChange={e => setForm(p => ({ ...p, Igstsdcaamt: e.target.value }))} disabled={isBusy} />
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* TCS */}
                            <div className="p-4 space-y-3">
                                <div className="flex items-center justify-between gap-3">
                                    <span className="flex items-center gap-1.5 text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                                        <Percent className="w-3.5 h-3.5 text-indigo-500" /> TCS Applicable?<span className="text-rose-500">*</span>
                                    </span>
                                    <YesNoToggle value={form.IsTCSApplicable} onChange={v => setForm(p => ({ ...p, IsTCSApplicable: v }))} />
                                </div>

                                {form.IsTCSApplicable === 'Yes' && (
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label>TCS DCA</Label>
                                            <input type="text" value={form.TCSDCA} onChange={e => setForm(p => ({ ...p, TCSDCA: e.target.value }))} disabled={isBusy} className={inputCls} />
                                        </div>
                                        <div>
                                            <Label>TCS SDCA</Label>
                                            <input type="text" value={form.TCSSDCA} onChange={e => setForm(p => ({ ...p, TCSSDCA: e.target.value }))} disabled={isBusy} className={inputCls} />
                                        </div>
                                        <div className="col-span-2">
                                            <Label>TCS Amount</Label>
                                            <RupeeInput value={form.TCSAmount} onChange={e => setForm(p => ({ ...p, TCSAmount: e.target.value }))} disabled={isBusy} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Right: sticky summary + actions ──────────────────────── */}
                <div className="lg:sticky lg:top-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <CardHeader icon={CheckCircle} title="Invoice Summary" />
                        <div className="p-4 space-y-3">
                            <dl className="space-y-1.5 text-xs">
                                {[
                                    { label: 'Invoice Type', value: form.InvoiceSubType },
                                    { label: 'Cost Center',  value: form.CCCode },
                                    { label: 'Client',       value: clientLabel },
                                    { label: 'Sub Client',   value: form.SubClientcode },
                                    { label: 'Request No.',  value: form.RequestNumber },
                                    { label: 'Invoice No.',  value: form.ClientInvoiceNo },
                                    { label: 'Invoice Date', value: formatDateForAPI(form.InvoiceDate) },
                                ].map(({ label, value }) => (
                                    <div key={label} className="flex justify-between gap-3">
                                        <dt className="text-gray-500 dark:text-gray-400 flex-shrink-0">{label}</dt>
                                        <dd className="font-semibold text-gray-800 dark:text-gray-100 truncate text-right" title={value || ''}>{value || '—'}</dd>
                                    </div>
                                ))}
                            </dl>

                            <dl className="space-y-1.5 text-xs pt-3 border-t border-gray-100 dark:border-gray-700">
                                <div className="flex justify-between">
                                    <dt className="text-gray-500 dark:text-gray-400">Basic Value</dt>
                                    <dd className="font-semibold text-gray-800 dark:text-gray-100">₹{fmt(bvNum)}</dd>
                                </div>
                                <div className="flex justify-between">
                                    <dt className="text-gray-500 dark:text-gray-400">
                                        GST{form.IsGstApplicable === 'Yes' && gstDetected ? (form.Statecheck ? ' (CGST+SGST)' : ' (IGST)') : ''}
                                    </dt>
                                    <dd className="font-semibold text-gray-800 dark:text-gray-100">
                                        {form.IsGstApplicable === 'Yes' ? `₹${fmt(gstAmt)}` : (form.IsGstApplicable || '—')}
                                    </dd>
                                </div>
                                <div className="flex justify-between">
                                    <dt className="text-gray-500 dark:text-gray-400">TCS</dt>
                                    <dd className="font-semibold text-gray-800 dark:text-gray-100">
                                        {form.IsTCSApplicable === 'Yes' ? `₹${fmt(tcsAmt)}` : (form.IsTCSApplicable || '—')}
                                    </dd>
                                </div>
                            </dl>

                            <div className="px-4 py-3 rounded-lg bg-gradient-to-r from-indigo-500 to-violet-600 text-white">
                                <p className="text-indigo-100 text-[10px] font-semibold uppercase tracking-wider">Total Invoice Value</p>
                                <p className="text-2xl font-black">₹ {fmt(total)}</p>
                            </div>

                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={handleReset}
                                    disabled={isBusy}
                                    className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"
                                >
                                    <RotateCcw className="h-4 w-4" /> Reset
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    disabled={isBusy}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                >
                                    {saveLoading
                                        ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</>
                                        : <><Send className="h-4 w-4" /> Submit Invoice</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientScrapSaleInvoiceCreation;
